/**
 * <immersive-gallery> – rebrand product gallery (hero + arrows + chapter tabs + thumbnails + lightbox).
 * Markup: snippets/product-immersive-gallery.liquid
 */
class ImmersiveGallery extends HTMLElement {
  /** @type {AbortController | null} */
  #controller = null;
  /** @type {IntersectionObserver | null} */
  #observer = null;
  #index = 0;

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.track = /** @type {HTMLElement} */ (this.querySelector('.ig__track'));
    this.slides = /** @type {HTMLElement[]} */ (Array.from(this.querySelectorAll('.ig__slide')));
    this.thumbs = /** @type {HTMLButtonElement[]} */ (Array.from(this.querySelectorAll('[data-ig-thumb]')));
    this.tabs = /** @type {HTMLButtonElement[]} */ (Array.from(this.querySelectorAll('[data-ig-tab]')));
    this.thumbStrip = /** @type {HTMLElement | null} */ (this.querySelector('[data-ig-thumbs]'));
    this.lightbox = /** @type {HTMLDialogElement | null} */ (this.querySelector('[data-ig-lightbox]'));
    this.lightboxImg = /** @type {HTMLImageElement | null} */ (this.querySelector('[data-ig-lightbox-img]'));
    this.counter = this.querySelector('[data-ig-current]');

    if (!this.track || this.slides.length === 0) return;

    this.addEventListener(
      'click',
      (event) => {
        const target = /** @type {HTMLElement} */ (event.target);
        if (target.closest('[data-ig-prev]')) return this.go(this.#index - 1);
        if (target.closest('[data-ig-next]')) return this.go(this.#index + 1);
        if (target.closest('[data-ig-close]')) return this.closeLightbox();
        if (target.closest('[data-ig-expand]')) return this.openLightbox();

        const thumb = /** @type {HTMLElement | null} */ (target.closest('[data-ig-thumb]'));
        if (thumb) return this.go(Number(thumb.dataset.igThumb));

        const tab = /** @type {HTMLElement | null} */ (target.closest('[data-ig-tab]'));
        if (tab) {
          const first = this.slides.find((s) => s.dataset.chapter === tab.dataset.igTab);
          if (first) this.go(Number(first.dataset.index));
          return;
        }

        if (this.dataset.zoom === 'true' && target.matches('img.ig__media')) this.openLightbox();
      },
      { signal }
    );

    this.addEventListener(
      'keydown',
      (event) => {
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.go(this.#index - 1);
        } else if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.go(this.#index + 1);
        }
      },
      { signal }
    );

    this.lightbox?.addEventListener(
      'click',
      (event) => {
        if (event.target === this.lightbox) this.closeLightbox();
      },
      { signal }
    );
    this.lightbox?.addEventListener('close', () => (document.documentElement.style.overflow = ''), { signal });

    // Keep state in sync with native swiping / scrolling of the track.
    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.6) {
            this.#setActive(Number(/** @type {HTMLElement} */ (entry.target).dataset.index), false);
          }
        }
      },
      { root: this.track, threshold: [0.6] }
    );
    this.slides.forEach((slide) => this.#observer?.observe(slide));

    // Start on the selected variant's image when it isn't first.
    const startId = this.dataset.startMedia;
    const start = this.slides.find((s) => s.dataset.mediaId === startId);
    const startIndex = start ? Number(start.dataset.index) : 0;
    if (startIndex > 0) {
      this.track.style.scrollBehavior = 'auto';
      this.track.scrollLeft = this.slides[startIndex].offsetLeft;
      requestAnimationFrame(() => (this.track.style.scrollBehavior = ''));
    }
    this.#setActive(startIndex, false);
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#observer?.disconnect();
  }

  /** @param {number} index */
  go(index) {
    const total = this.slides.length;
    const next = ((index % total) + total) % total;
    const slide = this.slides[next];
    if (!slide) return;

    this.track.scrollTo({ left: slide.offsetLeft, behavior: this.#reducedMotion ? 'auto' : 'smooth' });
    this.#setActive(next, true);
  }

  /**
   * @param {number} index
   * @param {boolean} fromUser
   */
  #setActive(index, fromUser) {
    if (Number.isNaN(index)) return;
    this.#index = index;
    const slide = this.slides[index];
    const chapter = slide?.dataset.chapter;

    // Pause any video that has scrolled out of view.
    this.slides.forEach((s, i) => {
      if (i !== index) s.querySelector('video')?.pause();
    });

    this.thumbs.forEach((thumb, i) => thumb.setAttribute('aria-current', String(i === index)));
    this.tabs.forEach((tab) => tab.setAttribute('aria-selected', String(tab.dataset.igTab === chapter)));
    if (this.counter) this.counter.textContent = String(index + 1);

    // Keep active thumbnail visible inside the strip without moving the page.
    const activeThumb = this.thumbs[index];
    if (activeThumb && this.thumbStrip) {
      const strip = this.thumbStrip;
      const left = activeThumb.offsetLeft - strip.offsetLeft;
      const right = left + activeThumb.offsetWidth;
      if (left < strip.scrollLeft || right > strip.scrollLeft + strip.clientWidth) {
        strip.scrollTo({ left: left - 8, behavior: fromUser && !this.#reducedMotion ? 'smooth' : 'auto' });
      }
    }

    if (this.lightbox?.open) this.#updateLightbox();
  }

  openLightbox() {
    if (!this.lightbox) return;
    const slide = this.slides[this.#index];
    if (!slide?.querySelector('img.ig__media')) return; // videos play inline
    this.#updateLightbox();
    this.lightbox.showModal();
    document.documentElement.style.overflow = 'hidden';
  }

  closeLightbox() {
    if (!this.lightbox?.open) return;
    this.lightbox.close();
    document.documentElement.style.overflow = '';
    /** @type {HTMLElement | null} */ (this.querySelector('[data-ig-expand]'))?.focus();
  }

  #updateLightbox() {
    const img = /** @type {HTMLImageElement | null} */ (this.slides[this.#index]?.querySelector('img.ig__media'));
    if (!img || !this.lightboxImg) return;
    const largest = (img.getAttribute('srcset') || '').split(',').pop()?.trim().split(' ')[0];
    this.lightboxImg.src = largest || img.currentSrc || img.src;
    this.lightboxImg.alt = img.alt;
  }

  get #reducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }
}

if (!customElements.get('immersive-gallery')) {
  customElements.define('immersive-gallery', ImmersiveGallery);
}